import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { bootstrap, loadThemeId, saveThemeId, loadWallpaper, saveWallpaper } from '@ogaim/app';
import { setImagePicker } from '@ogaim/core';
import { ThemeProvider, THEMES, type ThemeId, type WallpaperId } from '@ogaim/ui';
import { useCssTheme } from './useCssTheme';
import { pickImageFromBrowser } from './imagePicker';
import { Desktop } from './Desktop';
import './desktop.css';
// Bundled rather than fetched from a font CDN: the page must render correctly
// on first paint, and a webfont request is one more thing to fail.
import w95faUrl from './assets/W95FA.otf?url';

/**
 * Cognito's storage interface is synchronous, which localStorage already is —
 * no mirror needed here, unlike the React Native apps.
 */
const webStorage = {
  setItem: (k: string, v: string) => window.localStorage.setItem(k, v),
  getItem: (k: string) => window.localStorage.getItem(k),
  removeItem: (k: string) => window.localStorage.removeItem(k),
  clear: () => window.localStorage.clear(),
};

function injectFont() {
  const style = document.createElement('style');
  style.textContent = `@font-face {
    font-family: 'W95FA';
    src: url('${w95faUrl}') format('opentype');
    font-display: block;
  }`;
  document.head.appendChild(style);
}

function Root() {
  const [ready, setReady] = useState(false);
  // Read before first paint so the chosen theme is never flashed over.
  const [themeId, setThemeIdState] = useState<ThemeId>(() => loadThemeId(webStorage));

  const setThemeId = (id: ThemeId) => {
    setThemeIdState(id);
    saveThemeId(webStorage, id);
  };

  const [wall, setWall] = useState(() => loadWallpaper(webStorage));
  const setWallpaper = (id: WallpaperId, customUri?: string | null) => {
    setWall({ id, customUri: customUri ?? (id === 'custom' ? wall.customUri : null) });
    saveWallpaper(webStorage, id, customUri);
  };

  useCssTheme(THEMES[themeId]);

  useEffect(() => {
    injectFont();
    bootstrap({ storage: webStorage });
    setImagePicker(pickImageFromBrowser);
    // Wait for the font before the first paint so the chrome never flashes in
    // Helvetica and reflows — the metrics differ enough to be obvious.
    const done = () => setReady(true);
    if ('fonts' in document) {
      document.fonts.load('11px W95FA').then(done, done);
    } else {
      done();
    }
  }, []);

  if (!ready) return null;
  return (
    <ThemeProvider
      themeId={themeId}
      setThemeId={setThemeId}
      wallpaperId={wall.id}
      customWallpaperUri={wall.customUri}
      setWallpaper={setWallpaper}
    >
      <Desktop />
    </ThemeProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);
